import { format } from "date-fns";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import { defaultButtonClasses } from "@/constants/ui";
import { isMemberAnAdmin, isMemberAPracticeManager, isSystemAccount } from "@/core/auth";
import { AlertCircle, AlertTriangle, Calendar, Clock, CheckCircle2 } from "lucide-react";
import { useMember } from "@/contexts/memberContext";
import { useChangeBookingFlow } from "./ChangeBookingFlowProvider";
import { useUpdateBookingDateTime } from "@/hooks/useUpdateBookingDateTime";
import { isBookingWithin24Hours } from "@/utils/bookingWithin24Hours";
import { canMakePastBookings } from "@/utils/userPermissions";
import { isPastDateBooking } from "@/utils/isPastDateBooking";
import type { CanMoveValidationResponse } from "@/types/CanMoveValidation";

type DateTimeConfirmationProps = {
  onClose?: () => void;
  validationResult: CanMoveValidationResponse;
  newDate: Date;
  newStartTime: string;
  newEndTime: string;
};

const DateTimeConfirmation = ({
  onClose,
  validationResult,
  newDate,
  newStartTime,
  newEndTime,
}: DateTimeConfirmationProps) => {
  const { booking, navigateTo, resetFlow } = useChangeBookingFlow();
  const updateBookingMutation = useUpdateBookingDateTime();
  const { member } = useMember();
  const [error, setError] = useState<string | null>(null);
  const [waive, setWaive] = useState(false);

  const isElevatedUser = useMemo(
    () => isMemberAnAdmin(member) || isMemberAPracticeManager(member) || isSystemAccount(member),
    [member],
  );

  // Whether the user can make past bookings (elevated = admin / PM / superadmin).
  // Matches the exact pattern used by DateTime.tsx.
  const isElevated = canMakePastBookings(member);

  // True when the new/selected date is in the past and the user is elevated.
  const today = new Date();
  const isPastMove = isPastDateBooking(isElevated, newDate, today);

  // Amendments within 24h of an occurrence are now allowed but still chargeable —
  // surface the heads-up so the member confirms with eyes open. Server re-checks
  // authoritatively and flags `wasChargedOnAmendment` on the persisted document.
  // NOTE: do NOT pass isElevatedUser — we want the raw window check so elevated users
  // see the waive toggle (same pattern as CancelBookingDialogProvider's willBeCharged).
  const within24 = useMemo(
    () => (booking ? isBookingWithin24Hours(booking, {}) : { isWithin24Hours: false }),
    [booking],
  );

  // Chargeable amendment: date changed (cross-day move) AND within 24h.
  // Mirrors the backend classification in PATCH /datetime.
  const amendmentIsChargeable = useMemo(() => {
    if (!within24.isWithin24Hours || !booking) return false;
    // The new date is different from the booking's genesis date — cross-day move.
    const bookingDateIso = format(booking.date instanceof Date ? booking.date : new Date(booking.date), 'yyyy-MM-dd');
    const newDateIso = format(newDate, 'yyyy-MM-dd');
    return bookingDateIso !== newDateIso;
  }, [within24.isWithin24Hours, booking, newDate]);

  const formatTime = (time: string) => {
    // Convert HHmm to HH:mm
    return `${time.slice(0, 2)}:${time.slice(2)}`;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-GB", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleConfirm = async () => {
    if (!booking) return;

    // Clear any previous errors
    setError(null);

    try {
      // Format the date to ISO string (YYYY-MM-DD)
      const formattedDate = format(newDate, "yyyy-MM-dd");

      console.log("Updating booking:", {
        bookingId: booking.id,
        newDate: formattedDate,
        newStartTime,
        newEndTime,
      });

      // Call the update booking API
      await updateBookingMutation.mutateAsync({
        bookingId: booking.id,
        newDate: formattedDate,
        newStartTime,
        newEndTime,
        ...(waive ? { waive: true } : {}),
      });

      console.log("Booking updated successfully");

      // Move to final confirmation screen
      navigateTo("confirmation");
    } catch (err) {
      console.error("Failed to update booking:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to update booking. Please try again.";
      setError(errorMessage);
    }
  };

  const handleCancel = () => {
    resetFlow();
    onClose?.();
  };

  if (!booking) {
    return (
      <div className="p-6">
        <p className="text-red-600">Error: No booking data available</p>
        <Button onClick={handleCancel} className="mt-4">
          Close
        </Button>
      </div>
    );
  }

  const conflictCount = validationResult.conflictingDates?.length || 0;
  const baseDateBlocked = !validationResult.validations.roomAvailability.valid;
  const hasRecurringWarnings = !baseDateBlocked && conflictCount > 0;
  const hasConflicts = baseDateBlocked;

  return (
    <div className="min-h-[90vh] lg:min-h-[70vh] w-full flex flex-col bg-white rounded-xl my-4">
      {/* Header - Mobile only */}
      <div className="lg:hidden p-4 py-6 flex flex-col circle-pattern-background circle-pattern-indigo-50">
        <h2 className="text-2xl font-semibold mb-2">
          {hasConflicts ? "Conflicts Detected" : "Confirm Date & Time Change"}
        </h2>
        <p className="text-gray-600 text-xs">
          {hasConflicts
            ? "There are scheduling conflicts with this change. Please review the details below."
            : "Review your changes and confirm to update your booking."}
        </p>
      </div>

      {/* Desktop grid layout */}
      <div className="hidden lg:grid lg:grid-cols-3 grow gap-1">
        {/* Left column - Information */}
        <div className="p-6 lg:rounded-lg flex flex-col circle-pattern-background circle-pattern-indigo-50">
          <h2 className="text-3xl font-semibold mb-4">
            {hasConflicts ? "Conflicts Detected" : "Confirm Changes"}
          </h2>
          <p className="mb-2 text-gray-600 text-sm">
            {hasConflicts
              ? `We found ${conflictCount} conflict${conflictCount !== 1 ? "s" : ""} when checking the next 12 weeks for your recurring booking. Please review the details and decide how to proceed.`
              : "Please review the changes below. Once confirmed, your booking will be updated to the new date and time."}
          </p>
        </div>

        {/* Right column - Confirmation Details */}
        <div className="bg-gray-50 rounded-lg lg:rounded-r-xl lg:col-span-2 p-6 border-l border-gray-200 shadow-sm">
          {renderContent()}
        </div>
      </div>

      {/* Mobile content area */}
      <div className="lg:hidden flex-1 bg-gray-50 p-6">{renderContent()}</div>
    </div>
  );

  function renderContent() {
    return (
      <div className="flex flex-col gap-4">
        {/* API Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Update Failed</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Status Alert */}
        {hasConflicts ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Cannot Move Booking</AlertTitle>
            <AlertDescription>
              Your booking cannot be moved to the selected date and time due to scheduling conflicts.
            </AlertDescription>
          </Alert>
        ) : hasRecurringWarnings ? (
          <Alert className="border-amber-200 bg-amber-50">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-900">Recurring Conflicts Found</AlertTitle>
            <AlertDescription className="text-amber-800">
              The selected time works for this date, but {conflictCount} future occurrence{conflictCount !== 1 ? "s" : ""} will clash.
              You can still proceed — the conflicting dates are listed below.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-900">Ready to Update</AlertTitle>
            <AlertDescription className="text-green-800">
              No conflicts found. Your booking can be safely moved to the new time.
            </AlertDescription>
          </Alert>
        )}

        {/* Current vs New Booking Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Current Booking */}
          <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Current Booking</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-gray-900">{formatDate(new Date(booking!.date))}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-gray-900">
                  {formatTime(booking?.startTime ?? "")} - {formatTime(booking?.endTime ?? "")}
                </span>
              </div>
            </div>
          </div>

          {/* New Booking */}
          <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-3 text-indigo-900">New Booking</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-indigo-600" />
                <span className="text-indigo-900 font-medium">{formatDate(newDate)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-indigo-600" />
                <span className="text-indigo-900 font-medium">
                  {formatTime(newStartTime)} - {formatTime(newEndTime)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Conflicting Dates (if any — red for blocking, amber for recurring warnings) */}
        {(hasConflicts || hasRecurringWarnings) && validationResult.conflictingDates && validationResult.conflictingDates.length > 0 && (
          <div className={`bg-white border-2 rounded-lg p-4 ${hasConflicts ? "border-red-200" : "border-amber-200"}`}>
            <h3 className={`text-lg font-semibold mb-3 ${hasConflicts ? "text-red-900" : "text-amber-900"}`}>
              {hasConflicts ? "Conflicting Dates" : "Recurring Conflicts"}
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {validationResult.conflictingDates.map((conflict, index) => (
                <div
                  key={index}
                  className={`flex flex-col gap-1 p-2 rounded border ${
                    hasConflicts ? "bg-red-50 border-red-100" : "bg-amber-50 border-amber-100"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <AlertCircle className={`w-4 h-4 flex-shrink-0 ${hasConflicts ? "text-red-600" : "text-amber-600"}`} />
                    <span className={`text-sm font-medium ${hasConflicts ? "text-red-900" : "text-amber-900"}`}>
                      {new Date(conflict.date).toLocaleDateString("en-GB", {
                        weekday: "short",
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                  </div>
                  <p className={`text-xs ml-6 ${hasConflicts ? "text-red-700" : "text-amber-700"}`}>{conflict.reason}</p>
                  {conflict.conflictingBooking && (
                    <p className={`text-xs ml-6 ${hasConflicts ? "text-red-600" : "text-amber-600"}`}>
                      Conflicts with: {conflict.conflictingBooking.member?.firstName}{" "}
                      {conflict.conflictingBooking.member?.lastName}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Within-24h charge notice — informational; the change is still allowed. */}
        {within24.isWithin24Hours && (
          <div
            className="bg-amber-50 border border-amber-200 rounded-lg p-3"
            data-testid="datetime-change-within-24h-warning"
          >
            <p className="text-sm text-amber-900">
              <strong>Heads up:</strong> This booking is within 24 hours. The original booking will still be charged
              even after this change — shortening or moving does not avoid the existing commitment.
              {waive && " — charge will be waived"}
            </p>
          </div>
        )}

        {/* Past-date warning — only shown to elevated users committing to a past date. */}
        {isPastMove && (
          <div
            className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-300 rounded-lg"
            data-testid="past-date-warning"
          >
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-900">
              You're moving this booking to a past date.
            </p>
          </div>
        )}

        {/* Staff waive toggle — only for elevated users making a chargeable amendment (cross-day). */}
        {isElevatedUser && amendmentIsChargeable && (
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              data-testid="edit-waive-checkbox"
              checked={waive}
              onCheckedChange={(v) => setWaive(Boolean(v))}
            />
            <span>Waive this charge (the member will not be charged).</span>
          </label>
        )}

        {/* Recurring Booking Note */}
        {booking && booking.recurrence && !hasConflicts && !hasRecurringWarnings && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-900">
              <strong>Note:</strong> This is a recurring booking. This change will apply to all future occurrences of this booking from the selected date onwards.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-2 mt-6">
          {!baseDateBlocked && (
            <Button
              className={`w-full ${defaultButtonClasses}`}
              onClick={handleConfirm}
              disabled={updateBookingMutation.isPending}
            >
              {updateBookingMutation.isPending ? (
                <>
                  <Spinner className="size-4 mr-2" />
                  Updating booking...
                </>
              ) : hasRecurringWarnings ? (
                "Confirm Change Despite Conflicts"
              ) : (
                "Confirm Change"
              )}
            </Button>
          )}
          <Button variant="ghost" className="w-full text-gray-600" onClick={handleCancel}>
            {baseDateBlocked ? "Close" : "Cancel"}
          </Button>
        </div>
      </div>
    );
  }
};

export default DateTimeConfirmation;
